import React from "react";
import { cn } from "../../lib/utils";
import type {
  WorkspaceHeaderProps,
  DashboardShellAction,
  DashboardShellTab,
  DashboardShellMetaItem
} from "./types";

export function WorkspaceHeader({
  eyebrow,
  title,
  description,
  metaItems = [],
  tabs = [],
  actions = [],
  onTabSelect,
  onAction,
  className,
}: WorkspaceHeaderProps) {
  const renderAction = (action: DashboardShellAction) => {
    return (
      <button
        key={action.id}
        onClick={() => !action.disabled && onAction?.(action.id)}
        disabled={action.disabled}
        className={cn(
          "inline-flex items-center h-8 px-3 text-xs font-medium rounded-md transition-colors",
          action.tone === "primary" 
            ? "bg-primary text-primary-foreground hover:bg-primary/90"
            : "border border-border bg-background hover:bg-muted",
          action.disabled && "opacity-50 cursor-not-allowed",
          "gap-2"
        )}
      >
        {action.icon && <span className="w-3.5 h-3.5">{action.icon}</span>}
        <span>{action.label}</span>
      </button>
    );
  };

  const renderTab = (tab: DashboardShellTab) => {
    return (
      <button
        key={tab.id}
        onClick={() => onTabSelect?.(tab.id)}
        className={cn(
          "inline-flex items-center px-4 py-3 text-sm font-medium border-b-2 transition-colors relative",
          tab.active
            ? "border-primary text-foreground"
            : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
        )}
      >
        {tab.label}
        {tab.count && (
          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground border border-border font-normal">
            {tab.count}
          </span>
        )}
      </button>
    );
  };

  const renderMeta = (item: DashboardShellMetaItem) => {
    return (
      <div key={item.id} className="flex flex-col min-w-[80px]">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {item.label}
        </span>
        <span className="text-xs font-medium truncate">{item.value}</span>
      </div>
    );
  };

  return (
    <div className={cn("bg-card px-6 pt-6 flex flex-col gap-6", className)}>
      {/* Top row: Identity & Actions */}
      <div className="flex items-start justify-between gap-8">
        <div className="flex flex-col min-w-0">
          {eyebrow && (
            <span className="text-xs font-medium text-primary mb-1">
              {eyebrow}
            </span>
          )}
          <h2 className="text-2xl font-bold tracking-tight text-foreground truncate">
            {title}
          </h2>
          {description && (
            <p className="mt-1 text-sm text-muted-foreground line-clamp-2 max-w-2xl">
              {description}
            </p>
          )}

          {/* Metadata items */}
          {metaItems.length > 0 && (
            <div className="flex items-center gap-6 mt-4">
              {metaItems.map(renderMeta)}
            </div>
          )}
        </div>

        {/* Global actions */}
        {actions.length > 0 && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {actions.map(renderAction)}
          </div>
        )}
      </div>

      {/* Bottom row: Tabs */}
      {tabs.length > 0 && (
        <div className="flex items-center border-b border-border -mx-6 px-6">
          <div className="flex items-center">
            {tabs.map(renderTab)}
          </div>
        </div>
      )}
    </div>
  );
}
